import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { MdQrCodeScanner } from 'react-icons/md';
import useQrScanner from './useQrScanner';

/**
 * Podgląd kamery z celownikiem. Skanuje TEN SAM kod, który ochrona czyta na bramie i który
 * pracownik dostaje w mailu z zaproszeniem: QR plakietki `https://<instancja>/q/<token64>`
 * (App\Entity\WorkerBadge w backendzie).
 *
 * Świadomie NIE parsujemy tu payloadu. Kształt kodu (token po `/q/`, goły hex, układ AZERTY,
 * fallback na numer karty) rozstrzyga JEDNA funkcja w backendzie — App\Service\GateScanCodeParser
 * — wołana przez `badge-identify`. Drugie, przeglądarkowe wcielenie tych reguł rozjechałoby się
 * z bramą przy pierwszej zmianie formatu. Stąd `onDecoded` dostaje SUROWY odczyt.
 *
 * `variant` (Marcin, 2026-09-17): domyslnie `"dialog"` — dokladnie dzisiejszy wyglad (Bootstrap
 * Modal z ciemnym tlem i przyciskiem Anuluj), uzywany przez App.js. Stanowisko rejestracyjne
 * (RegistrationStation.js) ma kamere/pole na kod ZAWSZE wlaczone jako czesc ekranu spoczynku —
 * Modal z pelnoekranowym `.modal-backdrop` (App.css, rgba(15,23,42,0.34) + blur) zakrywalby WTEDY
 * cale tlo zakladu i komunikat oczekiwania, ktore stanowisko ma pokazywac. `variant="embedded"`
 * renderuje DOKLADNIE TA SAMA logike skanowania (kamera, `useQrScanner`, blokada `decodedRef`,
 * pole reczne) bez ramki dialogu/backdropu — zero drugiej kopii skanera, tylko inny opakowujacy
 * kontener.
 *
 * `hideManualInput` (Marcin, runda 5, 2026-09-17): stanowisko rejestracyjne ma WLASNY globalny
 * nasluch klawiatury (`useStationScannerListener`) czytajacy czytnik HID bez potrzeby pola — a
 * czlowiek i tak nie jest w stanie odczytac/przepisac tresci kodu QR z ekranu. Pole w oknie
 * kamery zostaje wiec WYLACZNIE dla App.js (logowanie pracownika), gdzie kamera bywa jedynym
 * skanerem na urzadzeniu bez czytnika. Domyslnie `false` — zachowanie App.js NIE ZMIENIA SIE
 * ani o piksel (nie przekazuje tego propa). `true` ustawia tylko RegistrationStation.js.
 */
const QrScannerModal = ({ show, onDecoded, onClose, variant = 'dialog', hideManualInput = false }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  // DROGA BEZ KAMERY (Marcin, 2026-09-02): „a co jezeli uzytkownik nie zezwoli na dostep do kamery?
  // Albo kamera bedzie uszkodzona?". Pole na kod jest ZAWSZE — czyta z niego czytnik przemyslowy
  // (klawiaturowy: wpisuje kod i wysyla Enter) i tu samo mozna kod wkleic. Wartosc leci do
  // `onDecoded` SUROWA, dokladnie jak odczyt z kamery: ksztalt kodu rozstrzyga backend
  // (`GateScanCodeParser`), nie przegladarka.
  const [kodRecznie, setKodRecznie] = useState('');
  const poleKoduRef = useRef(null);
  const { scanning, error, start, stop } = useQrScanner();
  // Kamera trafia w kod kilkanaście razy na sekundę. Bez tej blokady jeden fizyczny skan wysłałby
  // serię identycznych żądań identyfikacji.
  const decodedRef = useRef(false);
  // `onDecoded` przychodzi z App.js jako świeża domknięta funkcja przy KAŻDYM renderze rodzica.
  // Trzymany w tablicy zależności efektu restartowałby kamerę po każdym renderze (miganie obrazu,
  // a na słabszym tablecie pętla start/stop). Efekt zależy więc tylko od `show`, a aktualną
  // funkcję czytamy z refa w chwili trafienia.
  const onDecodedRef = useRef(onDecoded);
  onDecodedRef.current = onDecoded;

  useEffect(() => {
    if (!show) {
      decodedRef.current = false;
      stop();
      return undefined;
    }

    let cancelled = false;
    // Element <video> montuje się razem z ciałem modala — start() wołamy po tym, jak ref jest
    // wypełniony, a nie w tym samym cyklu co otwarcie.
    const timer = setTimeout(() => {
      if (cancelled || !videoRef.current) return;
      start(videoRef.current, (value) => {
        if (decodedRef.current) return;
        decodedRef.current = true;
        stop();
        onDecodedRef.current(value);
      });
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      stop();
    };
  }, [show, start, stop]);

  // KURSOR W POLU = czytnik klawiaturowy ma gdzie wpisac kod. Dwa przypadki, w ktorych go dajemy:
  //   1. kamera padla — pole jest wtedy jedyna droga,
  //   2. urzadzenie NIE jest dotykowe (kiosk, tablet z czytnikiem, komputer ochrony) — tam czytnik
  //      jest normalnym scenariuszem, a klawiatury ekranowej nie ma.
  // Na telefonie z dzialajaca kamera kursora NIE zabieramy: klawiatura ekranowa zaslonilaby podglad.
  useEffect(() => {
    if (!show || hideManualInput || !poleKoduRef.current) return;
    const dotykowy = typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    if (error || !dotykowy) {
      poleKoduRef.current.focus();
    }
  }, [show, error, hideManualInput]);

  useEffect(() => {
    if (!show) setKodRecznie('');
  }, [show]);

  const wyslijKod = (e) => {
    if (e) e.preventDefault();
    const kod = kodRecznie.trim();
    if (!kod) return;
    // TA SAMA BLOKADA CO PRZY KAMERZE: kamera trafia w kod kilkanascie razy na sekunde i tick
    // detekcji rozpoczety chwile przed zatwierdzeniem formularza nadal przeszedlby przez
    // `decodedRef.current === false`. Bez tego jedno wejscie potrafi wyslac DWA zadania
    // `badge-identify`, a UI reaguje na dwie odpowiedzi identyfikacji.
    if (decodedRef.current) return;
    decodedRef.current = true;
    stop();
    onDecoded(kod);
  };

  const scannerBody = (
    <>
      {error ? (
        <div className="alert alert-warning mb-0" data-track-id="qr-scan-error">
          {t(`qr_scan_error_${error}`)}
        </div>
      ) : (
        <>
          <div className="lt-qr-viewport">
            {/* playsInline: bez tego iOS Safari otwiera wideo na pełnym ekranie zamiast
                renderować je w ramce. muted: warunek autoodtwarzania w każdej przeglądarce. */}
            <video ref={videoRef} className="lt-qr-video" muted playsInline autoPlay />
            <div className="lt-qr-frame" aria-hidden="true" />
          </div>
          <p className="text-center text-muted mt-3 mb-0">
            {scanning ? t('qr_scan_hint') : t('qr_scan_starting')}
          </p>
        </>
      )}

      {/* Pole jest POD podgladem i zostaje takze wtedy, gdy kamera padla — inaczej odmowa zgody
          zamyka jedyne wejscie z plakietki. Runda 5: na stanowisku rejestracyjnym pole jest
          bezuzyteczne (czlowiek nie odczyta tresci kodu QR) i pomijamy je przez `hideManualInput`
          — czytnik HID i tak wysyla kod globalnie, bez pola w ogole. */}
      {!hideManualInput && (
        <Form onSubmit={wyslijKod} className="mt-3" data-track-id="qr-manual-form">
          <Form.Label htmlFor="qr-kod-recznie" className="small text-muted mb-1">
            {t('qr_manual_label')}
          </Form.Label>
          <InputGroup>
            <Form.Control
              id="qr-kod-recznie"
              ref={poleKoduRef}
              value={kodRecznie}
              onChange={(e) => setKodRecznie(e.target.value)}
              placeholder={t('qr_manual_placeholder')}
              autoComplete="off"
              spellCheck={false}
              data-track-id="qr-manual-input"
            />
            <Button
              type="submit"
              variant="danger"
              disabled={!kodRecznie.trim()}
              data-track-id="qr-manual-submit"
            >
              {t('qr_manual_submit')}
            </Button>
          </InputGroup>
        </Form>
      )}
    </>
  );

  if (variant === 'embedded') {
    // Bez Modal/backdrop/naglowka/stopki — stanowisko NIE MA czego "anulowac" (skan jest
    // czescia ekranu bazowego, nie dialogiem nad nim). `show=false` nie renderuje nic: efekty
    // wyzej i tak zatrzymuja kamere, wiec nie ma czego pokazywac.
    if (!show) return null;
    return (
      <div className="lt-qr-embedded" data-testid="qr-scanner-embedded">
        {scannerBody}
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onClose} backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h6" className="d-flex align-items-center gap-2">
          <MdQrCodeScanner size={21} /> {t('qr_scan_title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {scannerBody}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-center">
        <Button variant="secondary" onClick={onClose} data-track-id="qr-scan-cancel">
          {t('qr_scan_cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QrScannerModal;
